import { useCallback, useEffect, useState } from 'react'
import type { TicketRecord } from '@shared/orders'
import { ordersApi } from '../lib/api'

/** One chat order, kept live: refetched whenever the server reports a change to it. */
export function useOrder(id: string | null): { ticket: TicketRecord | null; error: string | null; reload: () => Promise<void> } {
  const [ticket, setTicket] = useState<TicketRecord | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reload = useCallback(async () => {
    if (!id) return
    try {
      setTicket(await ordersApi.get(id))
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the order')
    }
  }, [id])
  useEffect(() => {
    if (!id) return
    void reload()
    let t: ReturnType<typeof setTimeout> | null = null
    const off = ordersApi.subscribe((changed) => {
      if (changed !== id) return
      if (t) clearTimeout(t)
      t = setTimeout(() => void reload(), 150)
    })
    return () => {
      off()
      if (t) clearTimeout(t)
    }
  }, [id, reload])
  return { ticket, error, reload }
}

/** All of the user's chat orders, kept live. */
export function useOrders(): { tickets: TicketRecord[] | null; error: string | null; reload: () => Promise<void> } {
  const [tickets, setTickets] = useState<TicketRecord[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const reload = useCallback(async () => {
    try {
      setTickets(await ordersApi.list())
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load orders')
    }
  }, [])
  useEffect(() => {
    void reload()
    let t: ReturnType<typeof setTimeout> | null = null
    const off = ordersApi.subscribe(() => {
      if (t) clearTimeout(t)
      t = setTimeout(() => void reload(), 300)
    })
    return () => {
      off()
      if (t) clearTimeout(t)
    }
  }, [reload])
  return { tickets, error, reload }
}
